/**
 * @module NotificationConfig
 * @package @geenius/notifications/shared
 * @description Provides configuration helpers for initializing the shared
 * notifications package. This module resolves defaults, validates the
 * configuration, and exposes a typed runtime error for configuration failures.
 */

import { ErrorCode, GeeniusError } from '@geenius/tools/errors'
import type {
  NotificationConfig,
  NotificationPreferences,
  NotificationQuietHours,
} from './types'
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES,
} from './types'

let configState: NotificationConfig | null = null

const DEFAULT_REALTIME = {
  enabled: true,
  transport: 'convex',
} satisfies NonNullable<NotificationConfig['realtime']>

const DEFAULT_NOTIFICATION_PREFERENCES: Omit<NotificationPreferences, 'userId'> = {
  channels: {
    'in-app': true,
    email: true,
    push: false,
    sms: false,
  },
  types: {
    info: true,
    success: true,
    warning: true,
    error: true,
    mention: true,
    invite: true,
    payment: true,
    system: true,
  },
  quietHours: {
    start: '22:00',
    end: '07:00',
  },
  timezone: 'UTC',
}

const DEFAULT_QUIET_HOURS: NotificationQuietHours = {
  start: '22:00',
  end: '07:00',
}

function resolveQuietHours(
  quietHours?: NotificationQuietHours,
): NotificationQuietHours {
  return {
    start: quietHours?.start ?? DEFAULT_QUIET_HOURS.start,
    end: quietHours?.end ?? DEFAULT_QUIET_HOURS.end,
  }
}

/**
 * Typed configuration error thrown when the notifications package is misused.
 */
export class NotificationsConfigError extends GeeniusError {
  /**
   * Creates a configuration error for the notifications package.
   *
   * @param message Human-readable explanation of the configuration issue.
   * @param context Optional contextual data that helps diagnose the issue.
   */
  constructor(message: string, context: Record<string, unknown> = {}) {
    super({
      code: ErrorCode.INVALID_CONFIG,
      message,
      context,
    })

    this.name = 'NotificationsConfigError'
    Object.setPrototypeOf(this, NotificationsConfigError.prototype)
  }
}

/**
 * Resolves a partial configuration against the package defaults.
 *
 * @param overrides Partial configuration supplied by the consumer.
 * @returns A fully resolved notification configuration.
 *
 * @example
 * ```ts
 * const config = defineNotificationsConfig({
 *   enabledChannels: ['in-app', 'email'],
 *   realtime: { enabled: true, transport: 'convex' },
 * })
 * ```
 */
export function defineNotificationsConfig(
  overrides: Partial<NotificationConfig> = {},
): NotificationConfig {
  const defaultPreferences: Omit<NotificationPreferences, 'userId'> = {
    channels: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.channels,
      ...overrides.defaultPreferences?.channels,
    },
    types: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.types,
      ...overrides.defaultPreferences?.types,
    },
    quietHours: resolveQuietHours(overrides.defaultPreferences?.quietHours),
    timezone:
      overrides.defaultPreferences?.timezone ??
      DEFAULT_NOTIFICATION_PREFERENCES.timezone,
  }

  const realtime = overrides.realtime
    ? {
        enabled: overrides.realtime.enabled,
        transport: overrides.realtime.transport,
        pollIntervalMs: overrides.realtime.pollIntervalMs,
      }
    : DEFAULT_REALTIME

  return {
    enabledChannels: overrides.enabledChannels
      ? [...overrides.enabledChannels]
      : [...NOTIFICATION_CHANNELS],
    defaultChannel: overrides.defaultChannel ?? 'in-app',
    realtime,
    defaultPreferences,
    providers: overrides.providers ? { ...overrides.providers } : {},
  }
}

/**
 * Merges an existing configuration with partial overrides.
 *
 * @param base Current resolved notification configuration.
 * @param overrides Partial configuration updates to apply.
 * @returns A new resolved notification configuration.
 */
export function mergeNotificationsConfig(
  base: NotificationConfig,
  overrides: Partial<NotificationConfig>,
): NotificationConfig {
  const basePreferences = base.defaultPreferences ?? DEFAULT_NOTIFICATION_PREFERENCES

  const defaultPreferences: Omit<NotificationPreferences, 'userId'> = {
    channels: {
      ...basePreferences.channels,
      ...overrides.defaultPreferences?.channels,
    },
    types: {
      ...basePreferences.types,
      ...overrides.defaultPreferences?.types,
    },
    quietHours: resolveQuietHours(
      overrides.defaultPreferences?.quietHours ?? basePreferences.quietHours,
    ),
    timezone: overrides.defaultPreferences?.timezone ?? basePreferences.timezone,
  }

  const baseRealtime = base.realtime ?? DEFAULT_REALTIME

  return defineNotificationsConfig({
    enabledChannels: overrides.enabledChannels ?? base.enabledChannels,
    defaultChannel: overrides.defaultChannel ?? base.defaultChannel,
    realtime: overrides.realtime
      ? {
          ...baseRealtime,
          ...overrides.realtime,
        }
      : baseRealtime,
    defaultPreferences,
    providers: { ...base.providers, ...overrides.providers },
  })
}

/**
 * Validates a resolved notification configuration.
 *
 * @param config Notification configuration to validate.
 * @throws {NotificationsConfigError} Thrown when the configuration is invalid.
 */
function validateNotificationConfig(config: NotificationConfig): void {
  if (config.enabledChannels.length === 0) {
    throw new NotificationsConfigError(
      'At least one notification channel must be enabled.',
    )
  }

  if (!config.enabledChannels.includes(config.defaultChannel)) {
    throw new NotificationsConfigError(
      'The default channel must be included in enabledChannels.',
      { defaultChannel: config.defaultChannel, enabledChannels: config.enabledChannels },
    )
  }

  if (
    config.realtime?.transport === 'polling' &&
    config.realtime.pollIntervalMs !== undefined &&
    config.realtime.pollIntervalMs < 5_000
  ) {
    throw new NotificationsConfigError(
      'Polling transport requires a poll interval of at least 5000ms.',
      { pollIntervalMs: config.realtime.pollIntervalMs },
    )
  }

  for (const channel of NOTIFICATION_CHANNELS) {
    if (config.defaultPreferences?.channels[channel] === undefined) {
      throw new NotificationsConfigError(
        'Default preferences must define all notification channels.',
        { channel },
      )
    }
  }

  for (const type of NOTIFICATION_TYPES) {
    if (config.defaultPreferences?.types[type] === undefined) {
      throw new NotificationsConfigError(
        'Default preferences must define all notification types.',
        { type },
      )
    }
  }
}

/**
 * Stores the resolved configuration for later package access.
 *
 * @param config Full or partial notification configuration.
 * @returns The resolved configuration stored by the package.
 * @throws {NotificationsConfigError} Thrown when the supplied config is invalid.
 *
 * @example
 * ```ts
 * configureNotifications({
 *   enabledChannels: ['in-app', 'email'],
 *   defaultChannel: 'in-app',
 *   realtime: { enabled: true, transport: 'convex' },
 * })
 * ```
 */
export function configureNotifications(
  config: NotificationConfig | Partial<NotificationConfig>,
): NotificationConfig {
  const resolvedConfig = defineNotificationsConfig(config)
  validateNotificationConfig(resolvedConfig)
  configState = resolvedConfig
  return resolvedConfig
}

/**
 * Returns the stored notifications configuration.
 *
 * @returns The currently configured notification settings.
 * @throws {NotificationsConfigError} Thrown when the package was not configured.
 */
export function getNotificationsConfig(): NotificationConfig {
  if (!configState) {
    throw new NotificationsConfigError(
      'Notifications have not been configured. Call configureNotifications() before reading the config.',
    )
  }

  return configState
}

/**
 * Reports whether the package currently has a stored configuration.
 *
 * @returns `true` when configureNotifications has been called.
 */
export function isNotificationsConfigured(): boolean {
  return configState !== null
}

/**
 * Clears the in-memory configuration state.
 *
 * @returns Nothing.
 */
export function resetNotificationsConfig(): void {
  configState = null
}
